import { useQuery } from '@tanstack/react-query';

import { useAxios } from './useAxios';

export interface LinearTeam {
  id: string;
  key: string;
  name: string;
}

export interface LinearWorkflowState {
  id: string;
  name: string;
  /** triage | backlog | unstarted | started | completed | canceled */
  type: string;
}

export interface LinearProject {
  id: string;
  name: string;
  slugId: string;
}

interface LinearSetupRequest {
  accountKey?: string;
  config?: Record<string, string>;
}

interface TeamsResponse {
  teams: LinearTeam[];
}

interface WorkflowStatesResponse {
  states: LinearWorkflowState[];
  suggestedOpenStateId?: string;
  suggestedCloseStateId?: string;
}

interface ProjectsResponse {
  projects: LinearProject[];
}

const ENABLED = (req: LinearSetupRequest) =>
  Boolean(req.accountKey) || Boolean(req.config?.apiKey);

const buildBody = (
  req: LinearSetupRequest,
  extra: Record<string, unknown> = {}
) => ({
  account: req.accountKey,
  secret: req.accountKey ? undefined : req.config,
  ...extra,
});

export const useLinearTeams = (req: LinearSetupRequest) => {
  const axios = useAxios();
  return useQuery({
    queryKey: [
      'linear',
      'teams',
      req.accountKey ?? 'new',
      req.config?.apiKey ?? '',
    ],
    queryFn: async (): Promise<LinearTeam[]> => {
      const response = await axios.post<TeamsResponse>(
        '/integration/linear/teams',
        buildBody(req)
      );
      return response.data?.teams ?? [];
    },
    enabled: ENABLED(req),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useLinearWorkflowStates = (
  req: LinearSetupRequest & { teamId?: string }
) => {
  const axios = useAxios();
  return useQuery({
    queryKey: [
      'linear',
      'workflow-states',
      req.teamId ?? '',
      req.accountKey ?? 'new',
    ],
    queryFn: async (): Promise<WorkflowStatesResponse> => {
      const response = await axios.post<WorkflowStatesResponse>(
        '/integration/linear/workflow-states',
        buildBody(req, { teamId: req.teamId })
      );
      return {
        states: response.data?.states ?? [],
        suggestedOpenStateId: response.data?.suggestedOpenStateId,
        suggestedCloseStateId: response.data?.suggestedCloseStateId,
      };
    },
    enabled: ENABLED(req) && Boolean(req.teamId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useLinearProjects = (
  req: LinearSetupRequest & { teamId?: string }
) => {
  const axios = useAxios();
  return useQuery({
    queryKey: ['linear', 'projects', req.teamId ?? '', req.accountKey ?? 'new'],
    queryFn: async (): Promise<LinearProject[]> => {
      const response = await axios.post<ProjectsResponse>(
        '/integration/linear/projects',
        buildBody(req, { teamId: req.teamId })
      );
      return response.data?.projects ?? [];
    },
    enabled: ENABLED(req),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
